<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Dropdown as VDropdown } from 'floating-vue';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: null,
  },
  labels: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: 'filled',
    validator: value => ['filled', 'dot'].includes(value),
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const isDropdownOpen = ref(false);

const currentLabel = computed(() =>
  props.labels.find(label => String(label.id) === String(props.modelValue))
);

const selectLabel = label => {
  emit('update:modelValue', label ? label.id : null);
  isDropdownOpen.value = false;
};
</script>

<template>
  <VDropdown
    v-model:shown="isDropdownOpen"
    :triggers="disabled ? [] : ['click']"
    placement="bottom-end"
    :distance="8"
    :arrow="false"
    popper-class="event-menu-popper"
  >
    <button
      type="button"
      class="font-semibold rounded-full flex items-center transition-all"
      :class="[
        variant === 'dot' && currentLabel
          ? 'h-6 px-2.5 gap-1.5 text-xs bg-n-slate-3 text-n-slate-12 hover:bg-n-slate-4'
          : variant === 'dot'
            ? 'h-6 px-2.5 text-xs bg-woot-50 text-woot-600 hover:bg-woot-100 dark:bg-woot-900/30 dark:text-woot-300 dark:hover:bg-woot-900/50'
            : 'h-10 px-4 text-sm gap-2',
        variant !== 'dot' && currentLabel
          ? 'text-white'
          : variant !== 'dot'
            ? 'bg-n-slate-3 text-n-slate-11 dark:bg-n-solid-3'
            : '',
        { 'opacity-50 cursor-not-allowed': disabled },
      ]"
      :style="
        variant !== 'dot' && currentLabel
          ? { backgroundColor: currentLabel.color }
          : {}
      "
      :disabled="disabled"
      @click.stop
      @mousedown.stop
    >
      <span
        v-if="variant === 'dot' && currentLabel"
        class="size-2 rounded-full flex-shrink-0"
        :style="{ backgroundColor: currentLabel.color }"
      />
      <span class="truncate max-w-32">
        {{
          currentLabel
            ? currentLabel.title
            : variant === 'dot'
              ? t('EVENT_LABEL_MGMT.DROPDOWN.ADD_LABEL')
              : t('EVENT_LABEL_MGMT.DROPDOWN.NO_LABEL')
        }}
      </span>
      <Icon
        v-if="variant !== 'dot'"
        :icon="isDropdownOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        class="size-4"
      />
    </button>
    <template #popper>
      <div
        class="bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-75 dark:border-slate-800 p-2 flex flex-col gap-1 min-w-48"
        @click.stop
        @mousedown.stop
      >
        <button
          type="button"
          class="w-full max-w-80 text-left px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center"
          :class="{
            'font-semibold text-slate-900 dark:text-slate-400 bg-slate-50 dark:bg-slate-700':
              !currentLabel,
          }"
          @click="selectLabel(null)"
        >
          {{ t('EVENT_LABEL_MGMT.DROPDOWN.NO_LABEL') }}
        </button>
        <button
          v-for="label in labels"
          :key="label.id"
          v-tooltip.left="label.description"
          type="button"
          class="w-full max-w-80 text-left px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2"
          :class="{
            'font-semibold text-slate-900 dark:text-slate-400 bg-slate-50 dark:bg-slate-700':
              String(currentLabel?.id) === String(label.id),
          }"
          @click="selectLabel(label)"
        >
          <span
            class="w-3 h-3 rounded-full flex-shrink-0"
            :style="{ backgroundColor: label.color }"
          />
          <span class="truncate">{{ label.title }}</span>
        </button>
      </div>
    </template>
  </VDropdown>
</template>
